/**
 * ===========================================================================
 * statusPalette.ts - ONE SOURCE FOR THE SIX STATUS COLOURS
 *
 * ---- ⛔⛔ THIS FILE WAS DESTROYED AND REBUILT, 2026-09-25 -----------------
 *
 * On 2026-09-25 a "new" module was written at this path by a create that was
 * never checked against the folder. The module already here - `hexToChannels`,
 * `applyStatusPalette`, the CSS-variable machinery - was overwritten whole.
 * `npm run build` found it (TS2724 in StatusColorsAdmin.tsx), `git show
 * HEAD:web/src/lib/statusPalette.ts` recovered it, and this file is the MERGE.
 *
 *   ** A CREATE IS A BLIND OVERWRITE WHENEVER THE NAME IS ALREADY TAKEN. **
 *
 * ⚠️ EVERYTHING BELOW MARKED "ORIGINAL" IS RESTORED VERBATIM AND MUST STAY
 *    THAT WAY. The additions sit alongside it, not instead of it.
 *
 * ---- ⭐ WHAT THE ORIGINAL DOES (RESTORED) --------------------------------
 *
 * Applies the DB-configured colours (dock.StatusColor, migration 135) to the
 * page as CSS custom properties.
 *
 * Why variables rather than re-rendering with inline colours: every surface
 * already styles itself with `bg-status-arrived/85` and friends, and those
 * Tailwind classes now resolve to `rgb(var(--status-arrived) / 0.85)`. Setting
 * the variable once on <html> retints the Gantt, week board, month grid,
 * agenda, dialogs and the dockview board without touching a single component.
 *
 * Channels, not hex: Tailwind's opacity modifiers need addressable RGB
 * channels. `#047857` in a var would silently break every /85 and /40 tile.
 *
 * Failure is deliberately silent - index.css ships the same values as
 * defaults, so an unreachable config endpoint leaves the app looking exactly
 * as it does today rather than unstyled.
 *
 * ---- ⭐ WHAT WAS ADDED 2026-09-25 ----------------------------------------
 *
 * > Pete, 2026-09-25: *"Approved is the wrong color. Needs to match our
 * > status colors in dock scheduler. Same on carrier screen, all the status
 * > need to match across the board."*
 *
 * Not a shade. **APPROVED AND ARRIVED WERE EXACTLY SWAPPED** on two separate
 * surfaces, and had been since the 2026-08-12 palette change:
 *
 *                     dock.StatusColor      kiosk index.html   CarrierDashboard
 *   APPROVED          #1d4ed8  blue         #047857  green     bg-green-100
 *   ARRIVED           #047857  green        #1d4ed8  blue      bg-blue-100
 *
 * ⚠️ THE KIOSK EVEN SAID IT WAS FINE. `KIOSK_STATUS_ORDER` carried the comment
 * *"Pill colors match the dock schedule"* the entire time. It was true the day
 * it was written.
 *   ** A COPY OF A VALUE THAT THE SOURCE MADE EDITABLE WILL GO STALE, AND THE
 *      COMMENT SAYING IT MATCHES WILL GO STALE WITH IT. **
 *
 * ⛔ THE CSS-VARIABLE PATH ABOVE COULD NOT SERVE THOSE TWO SURFACES. The
 * carrier portal draws `bg-green-100 text-green-800` Tailwind pills, not
 * `bg-status-*` classes, and the kiosk is a separate HTML app that never loads
 * this bundle at all. They each kept a PRIVATE COPY of the hexes instead.
 *
 *   >> SO THIS MODULE NOW HAS TWO OUTPUTS FROM ONE FETCH:
 *      1. the CSS variables, for every surface already wired to them (ORIGINAL)
 *      2. a palette VALUE, for surfaces that must compute a colour in JS (NEW)
 *
 * ⚠️ TREATMENT STAYS PER-SURFACE, ON PURPOSE. 135 stores TWO hexes per status
 * because "the scheduler is a light page and the dockview board is an
 * always-on dark TV read from 15-40 ft". This module hands out the HUE; each
 * surface decides whether that hue becomes a solid pill, a light tint or a
 * card border.
 *   ** ONE SOURCE OF TRUTH IS ABOUT THE VALUE, NOT ABOUT THE TREATMENT. **
 * ===========================================================================
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { getStatusColors, type StatusColorDto } from '@/api/client';

/* =========================================================================
 * ORIGINAL - RESTORED VERBATIM FROM HEAD. Do not "tidy" this section.
 * ========================================================================= */

/** '#047857' -> '4 120 87'. Returns null for anything that isn't #rrggbb. */
export function hexToChannels(hex: string | null | undefined): string | null {
    if (!hex) return null;
    const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

/** StatusCode -> the CSS variable each surface reads. */
const SCHEDULER_VAR: Record<string, string> = {
    REQUESTED: '--status-requested',
    APPROVED:  '--status-approved',
    ARRIVED:   '--status-arrived',
    DEPARTED:  '--status-departed',
    NOSHOW:    '--status-noshow',
    CANCELLED: '--status-cancelled',
};
const BOARD_VAR: Record<string, string> = {
    APPROVED:  '--board-approved',
    ARRIVED:   '--board-arrived',
    WORKED:    '--board-worked',
    HOLDING:   '--board-holding',
    // Board-visible since migration 137 (stand-downs); given board hexes by 138.
    // Two entries, not one: the scheduler has distinguished crimson no-show from
    // grey cancelled since 135, and the board must not disagree with it.
    NOSHOW:    '--board-noshow',
    CANCELLED: '--board-cancelled',
};

/**
 * ⚠️ THE CSS WRITE, SPLIT OUT FROM THE EXPORTED `applyStatusPalette`.
 *
 * The exported function now does two things - paint the variables AND seed the
 * value cache - and `loadStatusPalette` needs only the first of them. Calling
 * the exported one from inside the fetch would have it reassign the very
 * promise the fetch is still building.
 *   ** A FUNCTION THAT GAINS A SECOND JOB STOPS BEING SAFE TO REUSE FOR ITS
 *      FIRST ONE. **
 *
 * The body is the ORIGINAL `applyStatusPalette`, unchanged.
 */
function applyToCssVars(colors: StatusColorDto[]): void {
    const root = document.documentElement;
    for (const c of colors) {
        const code = (c.statusCode ?? '').toUpperCase();

        const schedVar = SCHEDULER_VAR[code];
        const schedCh = hexToChannels(c.schedulerHex);
        if (schedVar && schedCh) root.style.setProperty(schedVar, schedCh);

        const boardVar = BOARD_VAR[code];
        const boardCh = hexToChannels(c.boardHex);
        if (boardVar && boardCh) root.style.setProperty(boardVar, boardCh);
    }
}

/**
 * Set the variables on <html> from a palette payload.
 *
 * ⛔ SIGNATURE AND BEHAVIOUR ARE THE ORIGINAL'S. StatusColorsAdmin.tsx:72 calls
 * this with the DTOs it just saved so the calendar behind the dialog retints
 * immediately. That call site is not to change.
 *
 * ⭐ WHAT IS NEW IS THE SECOND LINE: the saved values are also pushed into the
 * value cache. Without it a save would retint every CSS-variable surface and
 * leave `useStatusPalette` holding the colours from before the save - the same
 * two-copies-disagreeing fault this module exists to end, reintroduced inside
 * the module itself.
 *   ** A SECOND OUTPUT MUST BE INVALIDATED BY EVERYTHING THAT INVALIDATES THE
 *      FIRST, OR IT IS JUST A CACHE THAT GOES WRONG LATER. **
 */
export function applyStatusPalette(colors: StatusColorDto[]): void {
    applyToCssVars(colors);
    inflight = Promise.resolve(mergeOverShipped(colors));
}

/* =========================================================================
 * ADDED 2026-09-25 - the palette as a VALUE, for surfaces that compute in JS.
 * ========================================================================= */

export type StatusCode =
    | 'REQUESTED' | 'APPROVED' | 'ARRIVED'
    | 'DEPARTED'  | 'NOSHOW'   | 'CANCELLED';

export interface StatusPaletteEntry {
    /** '#rrggbb' for a light page. Null in the table means "not shown here". */
    schedulerHex: string | null;
    /** '#rrggbb' tuned for the dark TV board. */
    boardHex: string | null;
    displayName: string;
}

export type StatusPalette = Record<string, StatusPaletteEntry>;

/**
 * ⛔⛔ THE SHIPPED VALUES, AND THEY ARE A FALLBACK - NOT A DEFAULT.
 *
 * ⚠️ THE DIFFERENCE MATTERS. A default is what you use when nobody has chosen;
 * these are what the database currently holds, copied here so a failed fetch
 * paints the right colours instead of grey boxes. If an operator retunes
 * APPROVED on the admin screen, THESE BECOME STALE AND THAT IS CORRECT - they
 * are only ever reached when the live values could not be read at all.
 *
 * ⚠️ AND THEY ARE THE SAME VALUES index.css SHIPS AS CSS DEFAULTS. Two
 * fallbacks for one palette, which is tolerable only because both are dead
 * paths whenever the endpoint answers. If you retune one, retune the other.
 *
 * ⛔ THIS MAP MUST NEVER BE READ DIRECTLY BY A COMPONENT. Import
 * `useStatusPalette` or `statusHex`; a component that reaches in here is a
 * seventh private copy and we have just spent a day removing the first six.
 *   ** A FALLBACK THAT IS CONVENIENT TO USE DIRECTLY WILL BE USED DIRECTLY. **
 *
 * Source: migration 135, seeded with the values shipped 2026-08-12.
 */
const SHIPPED: Record<StatusCode, StatusPaletteEntry> = {
    REQUESTED: { schedulerHex: '#b45309', boardHex: null,      displayName: 'Requested' },
    APPROVED:  { schedulerHex: '#1d4ed8', boardHex: '#2c3849', displayName: 'Approved' },
    ARRIVED:   { schedulerHex: '#047857', boardHex: '#25b07f', displayName: 'Arrived' },
    DEPARTED:  { schedulerHex: '#78716c', boardHex: null,      displayName: 'Departed' },
    NOSHOW:    { schedulerHex: '#9f1239', boardHex: null,      displayName: 'No-show' },
    CANCELLED: { schedulerHex: '#57534e', boardHex: null,      displayName: 'Cancelled' },
};

/**
 * ⚠️ THE FETCHED ROWS LAY OVER THE SHIPPED ONES, THEY DO NOT REPLACE THE MAP.
 * 135 does not promise a row per status, and a status the table omits must
 * still have a name and a hue rather than become undefined mid-render.
 */
function mergeOverShipped(colors: StatusColorDto[]): StatusPalette {
    const out: StatusPalette = { ...SHIPPED };
    for (const c of colors) {
        const code = (c.statusCode ?? '').toUpperCase();
        if (!code) continue;
        out[code] = {
            schedulerHex: c.schedulerHex,
            boardHex:     c.boardHex,
            displayName:  c.displayName,
        };
    }
    return out;
}

/**
 * ⭐ ONE REQUEST PER TAB, SHARED BY EVERY CALLER.
 *
 * ⚠️ THE PROMISE IS CACHED, NOT THE RESULT. Three panels mounting in the same
 * frame must make ONE request, not three - and caching the resolved value only
 * would let all three fire before the first returns.
 *   ** DEDUPLICATING ON THE ANSWER STILL SENDS EVERY QUESTION. **
 */
let inflight: Promise<StatusPalette> | null = null;

/**
 * Fetch, apply to the CSS variables, and return the palette as a value.
 *
 * ⛔ main.tsx:14 CALLS THIS AS `void loadStatusPalette()` AT STARTUP AND THAT
 * LINE IS UNCHANGED. The original returned `Promise<void>`; this returns the
 * palette. `void` discards either one, and no other caller awaited it.
 *
 * ⚠️ THE CSS WRITE IS WHY THIS STAYS ONE FUNCTION. Splitting "load the value"
 * from "paint the variables" would mean two fetches of the same endpoint on
 * every page load, and a window in which they disagree.
 *
 * Never throws - a palette problem must not stop the app (or a wall display)
 * from rendering.
 */
export function loadStatusPalette(): Promise<StatusPalette> {
    if (inflight) return inflight;
    inflight = getStatusColors()
        .then(({ colors }) => {
            applyToCssVars(colors as StatusColorDto[]);
            return mergeOverShipped(colors as StatusColorDto[]);
        })
        .catch((err) => {
            /**
             * ⛔ A FAILED PALETTE IS NOT A FAILED SCREEN. The carrier portal
             * still has to list appointments if this endpoint is down; it just
             * lists them in last week's colours. The CSS variables are left
             * alone, so index.css's defaults stand - which is exactly what the
             * original's silent `catch {}` did.
             *
             * ⚠️ `inflight` IS CLEARED so the next mount retries. Leaving a
             * rejected promise cached would make one network blip permanent
             * for the whole tab.
             */
            console.warn('[statusPalette] falling back to shipped values', err);
            inflight = null;
            return { ...SHIPPED } as StatusPalette;
        });
    return inflight;
}

/** Test seam and a hard reset. */
export function invalidateStatusPalette(): void {
    inflight = null;
}

/**
 * ⭐ THE HOOK EVERY JS-COMPUTED SURFACE SHOULD USE.
 *
 * ⚠️ IT RETURNS THE SHIPPED VALUES ON THE FIRST RENDER, NOT NULL AND NOT A
 * SPINNER. A status chip that is briefly absent, grey, or replaced by a loader
 * is a worse first paint than one that is briefly last-week's colour - and on
 * the overwhelmingly common path the two are identical, because the database
 * holds exactly these values.
 *   ** A CACHE WARMING UP SHOULD LOOK LIKE THE ANSWER, NOT LIKE A QUESTION. **
 *
 * ⛔ THE `alive` FLAG IS NOT CEREMONY. The carrier portal unmounts this on
 * sign-out, and a resolve after unmount is a React warning plus a leak.
 *
 * ⚠️ NO DEPENDENCY ON THE PALETTE ITSELF. `loadStatusPalette` dedupes, so ten
 * components calling this hook in one frame make one request between them.
 */
export function useStatusPalette(): StatusPalette {
    const [palette, setPalette] = useState<StatusPalette>(SHIPPED);
    useEffect(() => {
        let alive = true;
        void loadStatusPalette().then((p) => { if (alive) setPalette(p); });
        return () => { alive = false; };
    }, []);
    return palette;
}

/**
 * The hue for a status on a light surface.
 *
 * ⚠️ AN UNKNOWN CODE RETURNS THE CANCELLED GREY RATHER THAN THROWING. Dock has
 * exactly six statuses and adding a seventh is a deliberate migration (135 says
 * so), but a stale tab holding an appointment whose status was renamed must
 * still paint something.
 */
export function statusHex(palette: StatusPalette, code: string): string {
    return palette[code]?.schedulerHex
        ?? SHIPPED.CANCELLED.schedulerHex
        ?? '#57534e';
}

export function statusName(palette: StatusPalette, code: string, fallback: string): string {
    return palette[code]?.displayName ?? fallback;
}

/**
 * ⭐ A LIGHT-PAGE CHIP, DERIVED FROM THE ONE HUE.
 *
 * The carrier portal and the scheduler both draw a pale chip with dark text -
 * `bg-green-100 text-green-800` in Tailwind terms. Rebuilding that from a hex
 * with `color-mix` keeps the existing LOOK while making the HUE come from the
 * database, which is the whole point of this change.
 *
 * ⛔ THE TEXT IS THE FULL-STRENGTH HUE, NOT BLACK. A 12%-tinted background with
 * its own hue at full strength on top is what `bg-x-100 / text-x-800` actually
 * is, and it keeps contrast well clear of 4.5:1 on white for all six values.
 *
 * ⚠️ `color-mix` HAS A STATIC FALLBACK because the carrier portal is opened on
 * whatever browser a dispatcher has. The fallback is the flat hue at low alpha,
 * which is close enough that nobody would notice and never unreadable.
 */
export function lightChipStyle(hex: string): CSSProperties {
    return {
        backgroundColor: `${hex}1f`,
        background: `color-mix(in srgb, ${hex} 12%, white)`,
        color: hex,
        border: `1px solid ${hex}59`,
    };
}
